import { stubFalse, stubTrue } from 'lodash';
import React, { useContext, useEffect, useMemo, useRef } from 'react';
import { FormattedMessage } from 'react-intl';
import { grey } from '@material-ui/core/colors';
import {
  ActionButtonHandle,
  FormContext,
  FormFieldContext,
  FormPanel,
  FormTableField,
  TableFieldToolbarProps,
} from '@eas/common-web';
import { SubjectPreselectContactContext } from '@modules/subject/subject-toolbar';
import { Contact } from '@models';
import { useContactColumns } from './contact-columns';
import { ContactDialog } from './contact-dialog';
import { ContactToolbar } from './contact-toolbar';

export interface ContactFieldProps {
  disabled?: boolean;
  display: 'IN_SUBJECT' | 'OUT_OF_SUBJECT';
  onRowSelect?: (row: Contact | null, index?: number) => void;
}

export function ContactField({
  disabled,
  display,
  onRowSelect,
}: ContactFieldProps) {
  const formContext = useContext(FormContext);
  const preselectId = useContext(SubjectPreselectContactContext);
  const contacts = formContext?.getFieldValues().contacts as Contact[];
  const contactColumns = useContactColumns();

  const editRef = useRef<ActionButtonHandle>(null);
  const formContextRef = useRef(formContext);
  formContextRef.current = formContext;
  const onRowSelectRef = useRef(onRowSelect);
  onRowSelectRef.current = onRowSelect;
  const handledPreselectRef = useRef<string | null | undefined>();

  const ToolbarComponent = useMemo(
    () =>
      function Toolbar(props: TableFieldToolbarProps) {
        useEffect(() => {
          if (preselectId && handledPreselectRef.current !== preselectId) {
            const index = (contacts ?? []).findIndex(
              (c) => c.id === preselectId
            );
            if (index !== -1) {
              props?.setSelectedIndex?.(index);
              formContextRef.current?.setFieldValue(
                'selectedContact',
                (contacts ?? [])[index]
              );
              onRowSelectRef.current?.((contacts ?? [])[index]);
              handledPreselectRef.current = preselectId;
            }
          }
        }, [props.setSelectedIndex]);

        return (
          <ContactToolbar
            {...props}
            ref={editRef}
            contacts={contacts}
            display={display}
            disabled={disabled}
          />
        );
      },
    [disabled, contacts, display, preselectId]
  );

  return (
    <FormPanel
      label={
        <FormattedMessage
          id="ES__SUBJECTS__DETAIL__PANEL_TITLE__CONTACTS"
          defaultMessage="Kontakty"
        />
      }
      expandable={false}
    >
      <FormFieldContext.Provider value={{ disabled: false }}>
        <FormTableField
          name="contacts"
          columns={contactColumns}
          onSelect={(row) => {
            formContext.setFieldValue('selectedContact', row);
            onRowSelect?.(row);
          }}
          labelOptions={{ hide: true }}
          layoutOptions={{ noSpacing: true }}
          FormFieldsComponent={ContactDialog}
          ToolbarComponent={ToolbarComponent}
          showDetailBtnCond={stubFalse}
          showRadioCond={stubTrue}
          createRowStyle={(item: Contact) =>
            !item?.active
              ? {
                  color: grey[500],
                }
              : {}
          }
        />
      </FormFieldContext.Provider>
    </FormPanel>
  );
}
